/**
 * AudioManager - Audio Interface Connection and Analysis
 * Handles audio device detection, capture, and real-time frequency analysis.
 * Decoupled from DOM - updates state instead of manipulating elements directly.
 */
import { AUDIO_PROCESSING } from './AudioConstants.js';

export class AudioManager {
  constructor(state) {
    this.state = state;
    this.audioContext = null;
    this.analyser = null;
    this.microphone = null;
    this.mediaStream = null;
    this.isInitialized = false;
    this.isListening = false;

    this.availableInterfaces = [];
    this.selectedInterface = null;

    this.audioData = { overall: 0, rms: 0, peak: 0, frequency: 0 };

    this.fftSize = AUDIO_PROCESSING.fft.size;
    this.smoothing = AUDIO_PROCESSING.fft.smoothing;
    this.sensitivity = 1.0;

    this.frequencyData = null;
    this.timeData = null;

    this._setupStateSubscriptions();
  }

  _setupStateSubscriptions() {
    this.state.subscribe('audioEnabled', (enabled) => {
      if (enabled) this.startAudioCapture();
      else this.stopAudioCapture();
    });
    this.state.subscribe('audioSensitivity', (s) => { this.sensitivity = s; });
    this.state.subscribe('audioSmoothing', (s) => { this.smoothing = s; });
  }

  async initialize() {
    if (this.isInitialized) return;

    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = this.fftSize;
      this.analyser.smoothingTimeConstant = this.smoothing;

      this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
      this.timeData = new Uint8Array(this.analyser.frequencyBinCount);

      this.isInitialized = true;
      this.state.set('audioAvailable', true);

      await this.discoverAudioInterfaces();
      this._updateStatusDOM('No Audio Device', false);
    } catch (error) {
      console.error('Failed to initialize AudioManager:', error);
      this.state.set('audioAvailable', false);
    }
  }

  async discoverAudioInterfaces() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableInterfaces = devices
        .filter(d => d.kind === 'audioinput')
        .map((d, i) => ({
          id: d.deviceId,
          label: d.label || `Audio Interface ${i + 1}`,
          index: i,
          groupId: d.groupId
        }));

      this.state.set('availableAudioInterfaces', this.availableInterfaces);

      if (this.availableInterfaces.length > 0) {
        this.selectInterface(this.availableInterfaces[0]);
      }
    } catch (error) {
      console.error('Failed to discover audio interfaces:', error);
      this.availableInterfaces = [];
    }
  }

  selectInterface(audioInterface) {
    this.selectedInterface = audioInterface;
    this.state.set('selectedAudioInterface', audioInterface);
  }

  async startAudioCapture() {
    if (!this.isInitialized) await this.initialize();
    if (this.isListening || !this.selectedInterface) return;

    try {
      if (this.audioContext.state === 'suspended') await this.audioContext.resume();

      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { ideal: this.selectedInterface.id },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: { min: AUDIO_PROCESSING.channels.min, ideal: AUDIO_PROCESSING.channels.ideal },
          sampleRate: { min: AUDIO_PROCESSING.sampleRates.min, ideal: AUDIO_PROCESSING.sampleRates.ideal }
        }
      });

      this.microphone = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.microphone.connect(this.analyser);
      this.isListening = true;
      this.state.set('audioListening', true);
      this._updateStatusDOM(this.selectedInterface.label, true);
      this._analyzeLoop();

    } catch (error) {
      console.error('Failed to start audio capture:', error);
      this.state.set('audioListening', false);
      this._updateStatusDOM('No Audio Device', false);
    }
  }

  stopAudioCapture() {
    if (!this.isListening) return;
    this.isListening = false;
    this.state.set('audioListening', false);
    this._updateStatusDOM('No Audio Device', false);

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(t => t.stop());
      this.mediaStream = null;
    }
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }
  }

  _analyzeLoop() {
    if (!this.isListening || !this.analyser) return;

    this.analyser.getByteFrequencyData(this.frequencyData);
    this.analyser.getByteTimeDomainData(this.timeData);

    // RMS
    let rms = 0;
    for (let i = 0; i < this.timeData.length; i++) {
      const sample = (this.timeData[i] - AUDIO_PROCESSING.normalization.midiCenter) / AUDIO_PROCESSING.normalization.midiCenter;
      rms += sample * sample;
    }
    rms = Math.sqrt(rms / this.timeData.length);

    // Peak
    let peak = 0;
    for (let i = 0; i < this.timeData.length; i++) {
      const sample = Math.abs((this.timeData[i] - AUDIO_PROCESSING.normalization.midiCenter) / AUDIO_PROCESSING.normalization.midiCenter);
      if (sample > peak) peak = sample;
    }

    // Smooth
    const lerp = AUDIO_PROCESSING.smoothing.lerpFactor;
    this.audioData = {
      overall: this.audioData.overall + (rms * this.sensitivity - this.audioData.overall) * lerp,
      rms: this.audioData.rms + (rms * this.sensitivity - this.audioData.rms) * lerp,
      peak: this.audioData.peak + (peak * this.sensitivity - this.audioData.peak) * lerp,
      frequency: this._dominantFrequency()
    };

    // Push to state
    this.state.set('audioOverall', this.audioData.overall);
    this.state.set('audioRMS', this.audioData.rms);
    this.state.set('audioPeak', this.audioData.peak);
    this.state.set('audioFrequency', this.audioData.frequency);

    requestAnimationFrame(() => this._analyzeLoop());
  }

  _dominantFrequency() {
    let maxVal = 0, maxBin = 0;
    for (let i = 0; i < this.frequencyData.length; i++) {
      if (this.frequencyData[i] > maxVal) { maxVal = this.frequencyData[i]; maxBin = i; }
    }
    return (maxBin * this.audioContext.sampleRate) / this.fftSize;
  }

  _updateStatusDOM(label, connected) {
    const el = document.getElementById('audio-status');
    if (!el) return;
    const dot = el.querySelector('div.rounded-full');
    const text = el.querySelector('span');
    if (dot) {
      dot.classList.remove('bg-red-500', 'bg-green-500');
      dot.classList.add(connected ? 'bg-green-500' : 'bg-red-500');
    }
    if (text) text.textContent = label;
  }

  getAudioData() { return { ...this.audioData }; }
  getVolume() { return this.audioData.overall; }
  getAvailableInterfaces() { return [...this.availableInterfaces]; }
  getSelectedInterface() { return this.selectedInterface; }

  getRawFrequencyData() {
    return {
      frequencyData: this.frequencyData,
      sampleRate: this.audioContext?.sampleRate,
      fftSize: this.fftSize
    };
  }

  async refreshInterfaces() { await this.discoverAudioInterfaces(); }

  setAnalysisParams(fftSize, smoothing, sensitivity) {
    this.fftSize = fftSize;
    this.smoothing = smoothing;
    this.sensitivity = sensitivity;
    if (this.analyser) {
      this.analyser.fftSize = fftSize;
      this.analyser.smoothingTimeConstant = smoothing;
    }
  }

  destroy() {
    this.stopAudioCapture();
    if (this.audioContext && this.audioContext.state !== 'closed') this.audioContext.close();
    this.isInitialized = false;
  }
}
